import React from 'react';
import { useSelector } from 'react-redux';
import { useRequestData, usePrefs, useFlags, useLabels } from 'wsm-common-data';
import { isBrowser } from 'ws-scripts/modules/environment';
import { addNewRelicPageAction } from '@ddc/new-relic';
import { InvDataRefactored } from '../data';
import {
	getWISRequestBody,
	requestInventoryAndFacets
} from '../utilities/WISutils';
import { spellcheckProxyFetch } from '../utilities/freeFormSearchUtils';

/* eslint-disable react/no-danger */
const Widget = () => {
	const flags = useFlags();
	const labels = useLabels();

	const {
		WIS: ssrWISData,
		moreRequestData,
		moreRequestData: { isComposer }
	} = useSelector((state) => state);
	const requestData = {
		...useRequestData(),
		...moreRequestData
	};

	const prefs = usePrefs();

	const requestSpellcheck = (searchTerm) => {
		const { siteId } = requestData;
		const { 'listing.config.id': listingType } = prefs;
		return spellcheckProxyFetch(siteId, listingType, searchTerm, flags);
	};

	const requestWISInventoryAndFacets = (
		invParams,
		start,
		sortBy,
		eventType,
		eventData
	) => {
		// Add additionalInventoryFields needed by web integrations from the dataLayer
		if (isBrowser && window.DDC?.dataLayer?.additionalInventoryFields) {
			prefs['required.tracking.attributes'] =
				window.DDC.dataLayer.additionalInventoryFields.join(',');
		}

		const unifiedRequestBody = getWISRequestBody({
			requestData,
			prefs,
			invParams,
			start,
			sortBy,
			flags
		});
		return requestInventoryAndFacets(
			invParams,
			unifiedRequestBody,
			eventType,
			eventData
		);
	};

	if (isBrowser && !window.DDC?.InvData?.srpReady) {
		// SSR data is validated in exactly one place: InvDataRefactored's
		// _hydrateFromSSR, which always runs and treats null and malformed data
		// identically (skip hydration → client-side fallback fetch). So we pass
		// whatever the server dispatched straight through — ssrWISData is already
		// null when SSR didn't hydrate — rather than re-validating its shape here.

		// 'none' when SSR couldn't resolve a geoZip from URL, cookie, or
		// edgescape — used by the constructor to skip seeding geoData so the UI
		// stays honest about whether the SSR fetch was actually geo-bounded.
		const serverGeoZipSource = moreRequestData.serverGeoZipSource || 'none';
		// The actual zip the SSR fetch used. The constructor seeds geoData from
		// this (not localStorage) so the client matches what the page rendered.
		const serverResolvedGeoZip =
			moreRequestData.serverResolvedGeoZip || null;

		// Build with the given ssrData or null. Defined once so both the
		// initial attempt and the SSR-fallback retry share an identical
		// argument list — any future constructor arg lands in one place.
		const buildInvData = (ssrData) =>
			new InvDataRefactored(
				requestSpellcheck,
				requestWISInventoryAndFacets,
				prefs,
				labels,
				requestData.parameterMap || {},
				ssrData,
				serverGeoZipSource,
				serverResolvedGeoZip
			);

		try {
			window.DDC.InvData = buildInvData(ssrWISData);
		} catch (constructorError) {
			addNewRelicPageAction('WS INV DATA CONSTRUCTOR ERROR', {
				error: constructorError?.message,
				stack: constructorError?.stack?.slice(0, 500),
				hadSSRData: !!ssrWISData
			});
			// Retry without SSR data so the client-side fallback fetch fires
			window.DDC.InvData = buildInvData(null);
		}

		window.onpopstate = (e) => {
			if (e.state) {
				window.DDC.InvData.replaceSelections(
					e.state.urlParams,
					e.state.sortBy ? e.state.sortBy : '',
					e.state.start ? e.state.start : 0,
					false /* updateBrowserHistory */
				);
			} else {
				// When e.state is null (initial history entry, or entries pushed
				// before we started tracking state), reset to default filters.
				// Passing {} triggers a fresh WIS fetch using the server parameterMap
				// from requestData — this is correct because SRP URLs don't encode
				// all filter state, so parsing window.location would be unreliable.
				window.DDC.InvData.replaceSelections({}, '', 0, false);
			}
		};
	}

	if (!isComposer || !flags['srp-toggle-databus-editor']) {
		return null;
	}

	return (
		<>
			<p className="box box-border p-6 bg-primary text-center mt-4">
				<div data-display-sets="TITLE,IMAGE_ALT,IMAGE_TITLE,LISTING,CALLOUT" />
				<div className="composer-editor-only">
					<p
						dangerouslySetInnerHTML={{
							__html: labels.get(
								'INVENTORY_DATA_BUS_CONFIGURATION_VERBIAGE'
							)
						}}
					/>
				</div>
			</p>
		</>
	);
};

export default Widget;
